import { faPieChart as PieChartIcon } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import ContentNotification from '@/components/ContentNotfication';
import { PERMISSIONS } from '@/constants/auth.constants';
import { PRODUCTS } from '@/constants/products.constants';
import useProductsCheck from '@/hooks/useProductsCheck';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';

import { NoAccess } from '../NoAccess/NoAccess';
import { selectedOrganizationAtom } from '../Organizations';
import {
  DISABLE_SHIELD_SYNC_DURATION,
  DISABLE_SHIELD_SYNC_KEY
} from './VulnerabilityManagement.constants';
import {
  shieldSyncData,
  vmpWidgetsCollapsedAtom
} from './VulnerabilityManagement.store';

const VulnerabilityManagement = () => {
  const [widgetsCollapsed, setWidgetsCollapsed] = useAtom(
    vmpWidgetsCollapsedAtom
  );
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const hasProducts = useProductsCheck([PRODUCTS.VMPE]);
  const [disabledSyncButton, setDisabledSyncButton] = useState(() => {
    const lastClick = localStorage.getItem(DISABLE_SHIELD_SYNC_KEY);
    return (
      !!lastClick &&
      Date.now() - parseInt(lastClick, 10) < DISABLE_SHIELD_SYNC_DURATION
    );
  });
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSyncClick = () => {
    setDisabledSyncButton(true);
    localStorage.setItem(DISABLE_SHIELD_SYNC_KEY, Date.now().toString());
    shieldSyncData();

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      setDisabledSyncButton(false);
      syncTimeoutRef.current = null;
    }, DISABLE_SHIELD_SYNC_DURATION);
  };

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Re-enable the button once a cooldown carried over from a previous visit ends
  useEffect(() => {
    const lastClick = localStorage.getItem(DISABLE_SHIELD_SYNC_KEY);
    if (!lastClick) return;
    const remaining = Math.max(
      0,
      DISABLE_SHIELD_SYNC_DURATION - (Date.now() - parseInt(lastClick, 10))
    );
    const cooldownTimeout = setTimeout(
      () => setDisabledSyncButton(false),
      remaining
    );
    return () => {
      clearTimeout(cooldownTimeout);
    };
  }, []);

  return (
    <>
      {selectedOrganization.id !== 0 && !hasProducts && <NoAccess />}
      {(hasProducts || selectedOrganization.id === 0) && (
        <>
          <Header
            title="Vulnerability Management"
            actionsNewLine
            actions={
              <>
                <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
                  <Button
                    variant="contained"
                    onClick={handleSyncClick}
                    disabled={disabledSyncButton}
                  >
                    {disabledSyncButton ? 'Syncing...' : 'Sync Shield Data'}
                  </Button>
                </CheckPermissions>
                <Button
                  variant={widgetsCollapsed ? 'outlined' : 'contained'}
                  onClick={() => {
                    setWidgetsCollapsed((prev) => !prev);
                  }}
                >
                  <FontAwesomeIcon size="lg" icon={PieChartIcon} />
                </Button>
              </>
            }
          ></Header>
          <MainContent>
            {selectedOrganization.id === 0 && (
              <ContentNotification
                title="Select an organization"
                text="Please select an organization with a VMPE product to view vulnerabilities and assets."
              />
            )}
            {selectedOrganization.id !== 0 && hasProducts && <Outlet />}
          </MainContent>
        </>
      )}
    </>
  );
};

export default VulnerabilityManagement;
